import { engineComponent } from "neo-common";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { RouletteController } from "./RouletteController";
import { CustomEase } from "../../Components/Effects/CustomEase";
import { Grid } from "../Grid";
import { Wheel } from "../Wheel";
import { Bet } from "../Bet";
import { Racetrack } from "../Racetrack";
import { LayoutSwitchElement } from "../../CustomComponents/LayoutSwitchElement";
import { GameManager } from "../../Managers/GameManager";
import { GridMagnifier } from "../GridMagnifier";
import { WinAnimator, WinAnimatorAttributes } from "../WinAnimator";
import { Dolly } from "../Dolly";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";
import { ShapeElement } from "../../Components/Elements/Graphics/ShapeElement";
import { UserAction } from "../../HTMLComponents/UserAction";
import { DataManager } from "../../Managers/DataManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { Scaling } from "../../Utils/Scaling";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { RenderTextureElement } from "../../CustomComponents/RenderTextureElement";
import { LayoutManagerExtended } from "../../CustomComponents/LayoutManagerExtended";
import { StatisticsManager } from "../../Managers/StatisticsManager";
import { Configuration } from "../../Components/Configuration";
import { FrameTimerPool } from "../../Utils/FrameTimerPool";
import { StageController } from "../../Components/StageController";
import { CustomUtils } from "../../Utils/CustomUtils";
import { ModuleManager } from "../../Managers/ModuleManager";
import { Communication } from "neo-games";
import { GameApp } from "../../GameApp";

var GameModules = Communication.Game.GameModules;

@engineComponent(ComponentTypes.DisplayObject)
export class RouletteView extends ContainerElement {
    private _controller: RouletteController;

    private static LayoutManager: LayoutManagerExtended;
    private static ColorImageElements: Array<ImageElement> = [];
    private static ColorShapeElements: Array<ShapeElement> = [];

    private layout: LayoutSwitchElement;
    private grid: Grid;
    private wheel: Wheel;
    private racetrack: Racetrack;
    private winAnimator: WinAnimator;
    private dollyHolder: SwitchElement;
    private winNumberSwitch: SwitchElement;
    private winNumberRender: RenderTextureElement;
    private playWinNumber: boolean;

    private inAutoplayIdle: boolean;
    private racetrackEnabled: boolean;
    private baseLoopLength: number = 120000;

    private welcome: SwitchElement;

    constructor() {
        super();

        this.inAutoplayIdle = false;
        this.racetrackEnabled = false;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        RouletteView.LayoutManager = new LayoutManagerExtended();

        if (this.childs != null) {
            this.layout = <LayoutSwitchElement>this.childs["Layout"];
            this.welcome = <SwitchElement>this.childs["Welcome"];

            if (this.layout) {
                this.grid = <Grid>this.layout.getChildElementByName("Grid");
                this.wheel = <Wheel>this.layout.getChildElementByName("Wheel");
                this.racetrack = <Racetrack>this.layout.getChildElementByName("Racetrack");
                this.winAnimator = <WinAnimator>this.childs["WinAnimator"];

                let wheelWinNumberContainer = <ContainerElement>this.childs["WheelWinNumberContainer"];
                if (wheelWinNumberContainer != null) {
                    this.winNumberSwitch = <SwitchElement>wheelWinNumberContainer.childs["WheelWinNumber"];
                    if (this.winNumberSwitch != null) {
                        this.winNumberSwitch.on(
                            SwitchElement.FinishAnimation,
                            this.finishRenderWheelWinNumber.bind(this),
                        );
                        this.winNumberRender = <RenderTextureElement>(
                            this.winNumberSwitch.getChildElementByName("WinNumber")
                        );

                        if (this.winNumberRender != null) {
                            this.winNumberRender.initialize(this.layout);
                        }
                    }
                }

                if (this.grid != null) {
                    this.dollyHolder = <SwitchElement>this.grid.childs["DollyHolder"];
                }

                let background = <ImageElement>this.layout.getChildElementByName("Background");
                let overlay = <ImageElement>this.layout.getChildElementByName("Overlay");
                if (background != null && overlay != null) {
                    RouletteView.ColorImageElements.push(background);
                    RouletteView.ColorImageElements.push(overlay);
                }
            }
        }

        StatisticsManager.initialize();
        CustomEase._createOwnEasing();
        this.welcome.on(SwitchElement.FinishAnimation, this.enableControls.bind(this));
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: RouletteController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (GameManager.rouletteResponse && GameManager.rouletteResponse.initData) {
            if (this.grid != null) {
                this.grid.initialize();
            }

            if (this.racetrack != null) {
                this.racetrack.initialize();
            }

            if (this.wheel != null) {
                this.wheel.on(Wheel.WheelStopped, this.wheelStopped.bind(this));
                this.wheel.initialize(GameManager.rouletteResponse.statistics);
            }

            if (this.layout != null) {
                this.layout.on(LayoutSwitchElement.LayoutChange, this.layoutChange.bind(this));
                this.layout.on(LayoutSwitchElement.LayoutChangeFinished, this.layoutChangeFinished.bind(this));
            }

            if (this.winAnimator != null) {
                this.winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                this.winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                this.winAnimator.on(WinAnimator.FinishWinNumberAnimator, this.finishWinNumberAnimation.bind(this));
                this.winAnimator.initialize(
                    this.grid.getGridElements(),
                    this.grid.getBetElements(),
                    this.grid.getGridWinPoint(),
                    <Dolly>this.dollyHolder.getChildElementByName("Dolly"),
                );
            }

            this.initializeMagnifier();
            Scaling.resizeRoot(); //due to new generated elements
        }
    }

    racetrackToggle(enabled: boolean): void {
        if (this.layout != null) {
            this.racetrackEnabled = enabled;
            this.layout.goToCase(enabled ? RouletteViewTypes.RACETRACK : RouletteViewTypes.GRID);
        }
    }

    private layoutChange(inPortrait: boolean): void {
        GridMagnifier.orientationChanged(inPortrait);
    }

    private layoutChangeFinished(): void {
        //because we updating html elements, we have to wait for redraw scene at least one frame
        setTimeout(() => {
            if (this.racetrack != null) {
                this.racetrack.layoutChanged();
            }

            if (this.grid != null) {
                this.grid.layoutChanged();
            }
        }, 1);
    }

    initIntro(): void {
        this.playBaseLoopSwitch();
        setInterval(() => {
            this.playBaseLoopSwitch();
        }, this.baseLoopLength);
        this.layout.goToCase(RouletteViewTypes.GRID + "_INIT");
        this.dollyHolder.goToCase(RouletteViewTypes.GRID + "_INIT");
        this.layoutChangeFinished();
        this.intro();
    }

    collect(): void {}

    schemasToggle(): void {
        if (
            this.layout.getCurrentCase() != RouletteViewTypes.SCHEMAS &&
            this.layout.getCurrentCase() != RouletteViewTypes.SCHEMAS + "_INIT"
        ) {
            this.layout.goToCase(RouletteViewTypes.SCHEMAS);
        } else {
            this.layout.goToCase("FAST_" + RouletteViewTypes.GRID);
        }
    }

    placeGridBet(roulletBet: Bet): void {
        if (this.grid) {
            this.grid.placeGridBet(roulletBet);
        }
    }

    resetBets(): void {
        if (this.grid) {
            this.grid.resetBets();
        }
    }

    placeSchemaBet(roulletBet: Bet): void {
        if (this.grid) {
            this.grid.placeSchemaBet(roulletBet);
        }
    }

    resetSchemaBets(): void {
        if (this.grid) {
            this.grid.resetSchemaBets();
        }
    }

    updateSchemaPossibleWins(bets: Array<Bet>): void {
        if (this.grid) {
            this.grid.updateSchemaPossibleWin(bets);
        }
    }

    changeBet(): void {}

    playBaseLoopSwitch(): void {
        let chooseSong = Math.floor(Math.random() * 3) + 1;
        if (DataManager.AmbientSoundEnabled) {
            switch (chooseSong) {
                case 2:
                    DataManager.ActiveAmbientSound = 2;
                    PlayList.stop("SND_BaseLoop1", 60);
                    PlayList.loop("SND_BaseLoop2", 60);
                    PlayList.stop("SND_BaseLoop3", 60);
                    break;
                case 3:
                    DataManager.ActiveAmbientSound = 3;
                    PlayList.stop("SND_BaseLoop1", 60);
                    PlayList.stop("SND_BaseLoop2", 60);
                    PlayList.loop("SND_BaseLoop3", 60);
                    break;
                default:
                    DataManager.ActiveAmbientSound = 1;
                    PlayList.loop("SND_BaseLoop1", 60);
                    PlayList.stop("SND_BaseLoop2", 60);
                    PlayList.stop("SND_BaseLoop3", 60);
            }
        }
    }

    spin(): void {
        this.welcome.goToCase("STOP");
        this.inAutoplayIdle = false;

        this.wheel.spinStart();
        this.layout.goToCase(RouletteViewTypes.SPIN);
        this.dollyHolder.goToCase(RouletteViewTypes.SPIN);
    }

    stop(): void {
        this.wheel.spinStop(true);

        // PlayList.stop('SND_NoMoreBets' + (DataManager.CommentaryGender ? '_F' : '_M'));
    }

    spinReceived(): void {
        this.wheel.spinStop(false, true);
    }

    wheelStopped(): void {
        if (GameManager && GameManager.rouletteResponse) {
            this._controller.wheelStopped();
            this.showWheelWinNumber();
            this.winAnimator.showWinNumber(
                GameManager.rouletteResponse.wins,
                GameManager.rouletteResponse.totalWin,
                GameManager.rouletteResponse.chosenNumber,
            );
        }
    }

    private showWheelWinNumber(): void {
        if (this.winNumberSwitch != null && this.winNumberRender != null) {
            this.playWinNumber = true;

            //calculate final number position angle
            const rendererAngle = this.degrees2radians(
                (this.wheel.getCurrentRotation() + this.wheel.getFinalAngle() - 90) * -1,
            );
            this.winNumberRender.rotation = rendererAngle;

            this.renderWheelWinNumber();
            this.winNumberSwitch.goToCase(window["CHANNEL"] == "mobile" ? "SHOW_HIDE" : "SHOW");
        }
    }

    private finishRenderWheelWinNumber(): void {
        this.playWinNumber = false;
    }

    private renderWheelWinNumber(): void {
        if (this.playWinNumber && !FrameTimerPool.paused) {
            let position = this.wheel.getBallGlobalPosition();
            this.winNumberRender.updateRender(position.x, position.y);

            requestAnimationFrame(this.renderWheelWinNumber.bind(this));
        }
    }

    finishWinNumberAnimation(): void {
        this.layout.goToCase("FAST_" + RouletteViewTypes.GRID);
        this.grid.hideBets(GameManager.rouletteResponse.chosenNumber);
        this.winAnimator.showWins();
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (DataManager.Autoplay && window["CHANNEL"] != "mobile") {
            this.layout.goToCase(RouletteViewTypes.AUTOPLAY_IDLE);
            this.inAutoplayIdle = true;
        } else {
            this.layout.goToCase(this.racetrackEnabled ? RouletteViewTypes.RACETRACK : RouletteViewTypes.GRID);
        }

        if (this.winNumberSwitch.getCurrentCase() != "EMPTY") {
            this.winNumberSwitch.goToCase("HIDE");
        }

        this.dollyHolder.goToCase("BASE");

        this._controller.allWinsFinished();

        PlayList.play("SND_AntePlease" + (DataManager.CommentaryGender ? "_F" : "_M"));
    }

    autoplayStop(): void {
        if (this.inAutoplayIdle) {
            this.layout.goToCase(this.racetrackEnabled ? RouletteViewTypes.RACETRACK : RouletteViewTypes.GRID);
            this.inAutoplayIdle = false;
        }
    }

    resetWinAnimator(): void {}

    intro(): void {
        if (DataManager.NarratorLanguageLoaded) {
            let welcomeTimer =
                DataManager.MasterVolume == 0 || !DataManager.CommentarySoundEnabled
                    ? 0
                    : PlayList.Songs["SND_WelcomeER" + (DataManager.CommentaryGender ? "_F" : "_M")].getDuration() *
                          StageController.FPS +
                      15;
            this.welcome.setDynamicTime(welcomeTimer);
            this.welcome.goToCase("WELCOME" + (DataManager.CommentaryGender ? "_F" : "_M"));
        } else {
            setTimeout(() => {
                this.intro();
            }, 100);
        }
    }

    enableControls(): void {
        ModuleManager.restoreGameRound(GameModules.Roulette);
    }

    outro(): void {}

    outroFinished(): void {}

    initializeMagnifier(): void {
        if (window["CHANNEL"] == "mobile") {
            let magnifier: ContainerElement = new ContainerElement();
            let node: Element = document.createElement("Magnifier");
            node.setAttribute("Style", "CS_magnifier");

            magnifier.create(node);

            GridMagnifier.initialize(<ContainerElement>this, magnifier);
        }
    }

    changeTableColor(color: string): void {
        let theme = DataManager.TableColors[color];

        if (theme != null) {
            this.setTableColor(theme.shapeColor, theme.tintColor);
        }
    }

    private setTableColor(shapeColor: string, tintColor: string): void {
        let colorHEX = parseInt(tintColor, 16);
        let shapeColorHEX = parseInt(shapeColor, 16);

        for (let i = 0; i < RouletteView.ColorImageElements.length; i++) {
            RouletteView.ColorImageElements[i].tint = colorHEX;
        }

        for (let i = 0; i < RouletteView.ColorShapeElements.length; i++) {
            RouletteView.ColorShapeElements[i].tint = shapeColorHEX;
        }

        const ambientBackground = document.getElementById("ambientBackgroundBase");
        ambientBackground.style.backgroundColor = "#" + tintColor;
    }

    public static addColorImageElement(element: ImageElement): void {
        if (element != null) {
            RouletteView.ColorImageElements.push(element);
        }
    }

    public static addColorShapeElement(element: ShapeElement): void {
        if (element != null) {
            RouletteView.ColorShapeElements.push(element);
        }
    }

    changeNarratorLanguage(language: string): void {
        let currentLanguage: string;
        if (DataManager.NarratorLanguageFirstLoad) {
            if (GameApp.language.indexOf("-") == -1) {
                currentLanguage = GameApp.language.toUpperCase();
            } else {
                currentLanguage = GameApp.language.substring(0, GameApp.language.indexOf("-")).toUpperCase();
            }
            DataManager.NarratorLanguageFirstLoad = false;
        } else {
            currentLanguage = "obsolete";
        }
        let languageExists = DataManager.NarratorLanguages.indexOf(language);
        if (languageExists != -1 && language !== currentLanguage) {
            CustomUtils.soundLazyLoad(language);
        }
    }
}

const RouletteViewTypes = {
    RACETRACK: "RACETRACK",
    SPIN: "SPIN",
    GRID: "GRID",
    AUTOPLAY_IDLE: "AUTOPLAY_IDLE",
    SCHEMAS: "SCHEMAS",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Roulette/RouletteView.ts
 **/